
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DashboardSummary from '@/components/DashboardSummary';
import CandidateCard from '@/components/CandidateCard';
import { Users, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useJobs } from '../contexts/JobsContext';
import AnalyticsOverview from '@/components/analytics/AnalyticsOverview';
import { useEmployerAnalytics } from '@/hooks/useEmployerAnalytics';
import AddJobDialog from '@/components/AddJobDialog';

const mockStats = {
  totalCandidates: 145,
  newCandidates: 12,
  shortlisted: 32,
  reviewed: 97,
  rejected: 16,
  averageScore: 76,
};

const mockVideoSummary = {
  keyPoints: [
    "Has 5+ years experience with React and TypeScript",
    "Led a team of 3-7 developers on multiple projects",
    "Worked on large-scale e-commerce applications",
    "Experienced with CI/CD pipelines and DevOps"
  ],
  impressions: [
    "Communicated technical concepts clearly",
    "Showed enthusiasm when discussing past projects",
    "Maintained good eye contact throughout the video",
    "Used technical jargon appropriately",
    "Organized thoughts in a coherent manner"
  ],
  overallRating: 85,
  communicationScore: 87,
  confidenceScore: 82,
  enthusiasmScore: 86,
  recommendationText: "Consider for interview - candidate demonstrates strong technical background and communication skills"
};

const mockCandidates = [
  { 
    id: "1", 
    name: 'Sarah Johnson', 
    email: "sjohnson@example.com",
    phone: "555-123-4567",
    jobTitle: 'UX Designer', 
    experience: "4 years",
    currentCtc: "$85,000",
    expectedCtc: "$95,000",
    resumeUrl: "#",
    videoUrl: "#",
    appliedDate: "2 days ago",
    skills: ["Figma", "User Research", "Prototyping", "UI Design"],
    resumeSummary: "Experienced UX Designer with a background in creating user-centered designs for SaaS products. Proficient in research methodologies and design systems.",
    assessmentScore: 82,
    status: 'new' as 'new' | 'reviewed' | 'shortlisted' | 'rejected',
    jobId: "2",
    videoSummary: {
      ...mockVideoSummary,
      keyPoints: [
        "4+ years of experience in UX/UI design",
        "Worked with cross-functional teams on product design",
        "Experience with user research and usability testing",
        "Portfolio showcases strong visual design skills"
      ],
      impressions: [
        "Articulated design process clearly",
        "Demonstrated user-centered thinking",
        "Showed passion for design and user experience",
        "Presented portfolio examples effectively",
        "Good communication skills"
      ],
    }
  },
  { 
    id: "2", 
    name: 'Michael Chen', 
    email: "mchen@example.com",
    phone: "555-987-6543",
    jobTitle: 'Frontend Developer', 
    experience: "6 years",
    currentCtc: "$115,000",
    expectedCtc: "$130,000", 
    resumeUrl: "#",
    videoUrl: "#",
    appliedDate: "1 day ago",
    skills: ["React", "TypeScript", "Next.js", "Tailwind CSS"],
    resumeSummary: "Senior Frontend Developer specializing in React ecosystem. Has built and maintained multiple large-scale applications with focus on performance optimization.",
    assessmentScore: 94,
    status: 'shortlisted' as 'new' | 'reviewed' | 'shortlisted' | 'rejected',
    jobId: "1",
    videoSummary: {
      ...mockVideoSummary,
      overallRating: 92,
      communicationScore: 90,
      confidenceScore: 94,
      enthusiasmScore: 92,
      keyPoints: [
        "6+ years experience with React and TypeScript",
        "Led frontend architecture for e-commerce platform",
        "Experience with performance optimization",
        "Strong knowledge of modern JavaScript frameworks"
      ]
    }
  },
  { 
    id: "3", 
    name: 'Jessica Lee', 
    email: "jlee@example.com",
    phone: "555-456-7890",
    jobTitle: 'Product Manager', 
    experience: "5 years",
    currentCtc: "$120,000",
    expectedCtc: "$140,000",
    resumeUrl: "#",
    videoUrl: "#",
    appliedDate: "Today", 
    skills: ["Product Strategy", "Agile", "Data Analysis", "User Stories"],
    resumeSummary: "Product Manager with experience in B2B SaaS. Skilled in product discovery, roadmap planning, and working with engineering teams to deliver successful products.",
    assessmentScore: 88,
    status: 'reviewed' as 'new' | 'reviewed' | 'shortlisted' | 'rejected',
    jobId: "3",
    videoSummary: {
      ...mockVideoSummary,
      overallRating: 89,
      communicationScore: 95,
      confidenceScore: 88,
      enthusiasmScore: 84,
      keyPoints: [
        "5 years experience as a Product Manager",
        "Led product strategy for B2B SaaS platform",
        "Experience with agile methodologies",
        "Strong focus on data-driven decision making"
      ],
      recommendationText: "Strong potential - recommend proceeding to next stage"
    }
  },
  { 
    id: "4", 
    name: 'David Wilson', 
    email: "dwilson@example.com",
    phone: "555-234-5678",
    jobTitle: 'Data Scientist', 
    experience: "3 years",
    currentCtc: "$95,000",
    expectedCtc: "$110,000",
    resumeUrl: "#",
    videoUrl: "#",
    appliedDate: "3 days ago",
    skills: ["Python", "Machine Learning", "SQL", "Data Visualization"],
    resumeSummary: "Data Scientist with background in predictive modeling and data analysis. Experience with Python, SQL, and various ML frameworks.",
    assessmentScore: 76,
    status: 'rejected' as 'new' | 'reviewed' | 'shortlisted' | 'rejected',
    jobId: "1",
    videoSummary: {
      keyPoints: [
        "3 years of data science experience",
        "Experience with Python and ML libraries",
        "Worked on predictive modeling projects",
        "Background in statistical analysis"
      ],
      impressions: [
        "Appeared nervous throughout the video",
        "Technical explanations were unclear",
        "Had difficulty articulating past projects",
        "Limited eye contact",
        "Responses lacked specific examples"
      ],
      overallRating: 62,
      communicationScore: 58,
      confidenceScore: 61,
      enthusiasmScore: 67,
      recommendationText: "Not recommended at this time - candidate showed technical knowledge but poor communication skills"
    }
  },
];

const Dashboard = () => {
  const { getJobsByEmployer, jobs } = useJobs();
  const [stats, setStats] = useState(mockStats);
  const [allCandidates, setAllCandidates] = useState(mockCandidates);
  const [filteredCandidates, setFilteredCandidates] = useState(mockCandidates);
  const [activeTab, setActiveTab] = useState('overview');
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentEmployerId, setCurrentEmployerId] = useState<string>('employer1');

  useEffect(() => {
    const fetchUser = async () => {
      setIsLoading(true);
      try {
        if (supabase && supabase.auth && typeof supabase.auth.getUser === 'function') {
          const { data, error } = await supabase.auth.getUser();
          
          if (error) {
            console.error('Error fetching user:', error);
            setUser({ email: 'demo@trueapply.com', role: 'employer', id: 'employer1' });
            setCurrentEmployerId('employer1');
          } else if (data?.user) {
            setUser(data.user);
            setCurrentEmployerId(data.user.id || 'employer1');
          } else {
            setUser({ email: 'demo@trueapply.com', role: 'employer', id: 'employer1' });
            setCurrentEmployerId('employer1');
          }
        } else {
          console.warn('Supabase auth not initialized, using mock user data');
          setUser({ email: 'demo@trueapply.com', role: 'employer', id: 'employer1' });
          setCurrentEmployerId('employer1');
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        setUser({ email: 'demo@trueapply.com', role: 'employer', id: 'employer1' });
        setCurrentEmployerId('employer1');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUser();
  }, []);

  useEffect(() => {
    if (currentEmployerId) {
      localStorage.setItem('currentEmployerId', currentEmployerId);
      
      const employerJobs = getJobsByEmployer(currentEmployerId);
      const employerJobIds = employerJobs.map(job => job.id);
      
      const relevantCandidates = allCandidates.filter(
        candidate => employerJobIds.includes(candidate.jobId)
      );
      
      setFilteredCandidates(relevantCandidates);
      
      setStats({
        totalCandidates: relevantCandidates.length,
        newCandidates: relevantCandidates.filter(c => c.status === 'new').length,
        shortlisted: relevantCandidates.filter(c => c.status === 'shortlisted').length,
        reviewed: relevantCandidates.filter(c => c.status === 'reviewed').length,
        rejected: relevantCandidates.filter(c => c.status === 'rejected').length,
        averageScore: Math.round(
          relevantCandidates.reduce((sum, c) => sum + c.assessmentScore, 0) / 
          (relevantCandidates.length || 1)
        ),
      });
    }
  }, [currentEmployerId, allCandidates, getJobsByEmployer]);

  const handleCandidateStatusChange = (candidateId: string, newStatus: 'new' | 'reviewed' | 'shortlisted' | 'rejected') => {
    const updatedCandidates = allCandidates.map(candidate => 
      candidate.id === candidateId 
        ? { ...candidate, status: newStatus } 
        : candidate
    );
    
    setAllCandidates(updatedCandidates);
    toast.success(`Candidate status updated to ${getStatusDisplayText(newStatus)}`);
  };

  const getStatusDisplayText = (status: string): string => {
    switch (status) {
      case 'new':
        return 'Pending Review';
      case 'reviewed':
        return 'Reviewed';
      case 'shortlisted':
        return 'Shortlisted';
      case 'rejected':
        return 'Rejected';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const getEmployerJobs = () => {
    return getJobsByEmployer(currentEmployerId);
  };

  const analytics = useEmployerAnalytics(currentEmployerId, filteredCandidates);

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-apple-text mb-2">Employer Dashboard</h1>
        <p className="text-apple-text-secondary">
          Welcome back{user?.email ? `, ${user.email.split('@')[0]}` : ''}
        </p>
      </header>

      <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-white border border-gray-100 shadow-sm">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="candidates">Candidates</TabsTrigger>
          <TabsTrigger value="jobs">Job Listings</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <DashboardSummary stats={stats} />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xl">Recent Candidates</CardTitle>
                <CardDescription>Latest applications to your job listings</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-4">
                  {filteredCandidates.slice(0, 3).map(candidate => (
                    <li key={candidate.id} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-apple-text-secondary mr-3">
                          <Users size={18} />
                        </div>
                        <div>
                          <p className="font-medium text-apple-text">{candidate.name}</p>
                          <p className="text-sm text-apple-text-secondary">{candidate.jobTitle}</p>
                        </div>
                      </div>
                      <div className="flex items-center text-sm text-apple-text-secondary">
                        <Clock size={14} className="mr-1.5" />
                        <span>{candidate.appliedDate}</span>
                      </div>
                    </li>
                  ))}
                  {filteredCandidates.length === 0 && (
                    <li className="text-center py-4 text-apple-text-secondary">
                      No candidates have applied to your job listings yet.
                    </li>
                  )}
                </ul>
                <Button variant="ghost" className="w-full mt-4 text-apple-blue" onClick={() => setActiveTab('candidates')}>
                  View all candidates
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 flex flex-row justify-between items-center">
                <div>
                  <CardTitle className="text-xl">Your Job Listings</CardTitle>
                  <CardDescription>Currently open positions</CardDescription>
                </div>
                <AddJobDialog size="sm" />
              </CardHeader>
              <CardContent>
                <ul className="space-y-4">
                  {getEmployerJobs().map(job => (
                    <li key={job.id} className="p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-center">
                        <p className="font-medium">{job.title}</p>
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
                          {filteredCandidates.filter(c => c.jobId === job.id).length} applicants
                        </span>
                      </div>
                      <p className="text-sm text-apple-text-secondary mt-1">
                        {job.location} {job.remote && '• Remote'}
                      </p>
                    </li>
                  ))}
                  {getEmployerJobs().length === 0 && (
                    <li className="text-center py-4 text-apple-text-secondary">
                      You haven't added any job listings yet.
                    </li>
                  )}
                </ul>
                <Button variant="ghost" className="w-full mt-4 text-apple-blue" onClick={() => setActiveTab('jobs')}>
                  Manage job listings
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="candidates">
          <Card>
            <CardHeader>
              <CardTitle>Candidates</CardTitle>
              <CardDescription>
                Showing candidates for your job listings ({filteredCandidates.length})
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {filteredCandidates.map((candidate) => (
                  <CandidateCard 
                    key={candidate.id} 
                    candidate={candidate}
                    onStatusChange={handleCandidateStatusChange}
                  />
                ))}
                {filteredCandidates.length === 0 && (
                  <div className="text-center py-8 text-apple-text-secondary">
                    No candidates have applied to your job listings yet. 
                    Post more jobs or check back later.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="jobs">
          <Card>
            <CardHeader className="flex flex-row justify-between items-center">
              <div>
                <CardTitle>Job Listings</CardTitle>
                <CardDescription>Manage your open positions</CardDescription>
              </div>
              <AddJobDialog />
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Button onClick={() => window.location.href = '/job-management'}>
                  Manage Your Job Listings
                </Button>
              </div>
              {getEmployerJobs().length === 0 ? (
                <div className="text-center py-8 flex flex-col items-center">
                  <p className="text-center py-4 text-apple-text-secondary mb-4">
                    You haven't created any job listings yet. Add some jobs to attract candidates.
                  </p>
                  <AddJobDialog variant="outline" buttonClassName="w-full max-w-xs" />
                </div>
              ) : (
                <ul className="space-y-4">
                  {getEmployerJobs().map(job => (
                    <li key={job.id} className="p-4 border rounded-lg">
                      <div className="flex justify-between">
                        <div>
                          <h3 className="font-medium">{job.title}</h3>
                          <p className="text-sm text-apple-text-secondary">
                            {job.department} • {job.location} {job.remote && '(Remote)'}
                          </p>
                        </div>
                        <div>
                          <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                            job.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {job.status === 'active' ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics">
          <Card>
            <CardHeader>
              <CardTitle>Hiring Analytics</CardTitle>
              <CardDescription>Track and optimize your recruiting process</CardDescription>
            </CardHeader>
            <CardContent>
              <AnalyticsOverview stats={stats} analytics={analytics} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;
